<script>
import { useIndexStore } from '@src/store'
import LeftPanel from '@comp/map/LeftPanel.vue'
import TrafficMap from '@comp/map/TrafficMap.vue'
import { backUrl } from '@src/config.js'
import axios from 'axios'
import api from '@src/api.js'

export default {
  name: 'MapPage',
  components: {
    LeftPanel,
    TrafficMap,
  },
  emits: [
    'editLine',
  ],
  data () {
    return {
      lines: [],
    }
  },
  computed: {
    currentProject () {
      return Object.keys(this.store.project).length !== 0 ? this.store.project : null
    },
    hiddenIfNoCurrentProject () {
      return this.currentProject ? '' : 'hidden'
    },
    isProjectAvailable () {
      return this.projectStatus === 'available'
    },
    projectStatus () {
      return this.currentProject ? this.currentProject.project_state.description : null
    },
    isProjectRecording () {
      return this.projectStatus === 'recording'
    },
    store () {
      return useIndexStore()
    },
  },
  watch: {
    currentProject: {
      handler () {
        if (this.currentProject) {
          this.loadProjectLines(this.currentProject.id)
        }
      },
    },
  },
  mounted () {
    this.store.changeRoute(this.$options.name)
  },
  methods: {
    loadProjectLines (projectId) {
      this.lines = []
      const promises = []
      const draw_ids = []
      axios.get(backUrl + 'projects/' + projectId + '/lines').then(response => {
        for (const line of response.data) {
          const url = backUrl + 'projects/' + projectId + '/lines/' + line.id + '/segments'
          promises.push(api.get(url))
          draw_ids.push(line.draw_id)
        }
        return Promise.all(promises).then(values => {
          const allPoints = []
          for (const [i, resSegments] of values.entries()) {
            if (resSegments.length === 0) {
              continue
            }
            const newLine = {
              id: resSegments[0].line_id,
              draw_id: draw_ids[i],
              name: resSegments[0].line_name,
              segments: [],
            }
            for (const resSegment of resSegments) {
              allPoints.push([resSegment.origin.coordinates[0], resSegment.origin.coordinates[1]])
              allPoints.push([resSegment.destination.coordinates[0], resSegment.destination.coordinates[1]])
              const segment = {
                from: resSegment.origin.coordinates[1] + ',' + resSegment.origin.coordinates[0],
                to: resSegment.destination.coordinates[1] + ',' + resSegment.destination.coordinates[0],
                distance: resSegment.distance,
                distance_text: resSegment.distance_text,
                average_duration: resSegment.average_duration,
                average_duration_text: resSegment.average_duration_text,
                start_address: resSegment.start_address,
                end_address: resSegment.end_address,
                polyline: {
                  points: resSegment.polyline,
                },
              }
              newLine.segments.push(segment)
            }
            this.lines.push(newLine)
          }
        })
      })
    },
    onDeleteProject () {
      this.store.changeProject({})
      this.store.changeProjectId(-1)
    },
    onLinesModification (lines) {
      this.lines = lines
    },
    onEditLine (index) {
      const line = this.lines[index]
      this.$refs.trafficMap.editLine(line)
    },
    onValidateLines () {
      this.$refs.trafficMap.exitEditMode()
    },
    onUpdateLineName (lineName, index) {
      this.lines[index].name = lineName
    },
  },
}
</script>
<template>
  <section class="map-view">
    <div
      v-if="isProjectRecording"
      class="mapview-disabled"
    >
      <div>{{ $gettext('Recording in progress…') }}</div>
      <v-progress-circular
        :width="5"
        :size="60"
        color="primary"
        indeterminate
      />
    </div>
    <LeftPanel
      v-show="projectStatus"
      :current-project="currentProject"
      :lines="lines"
      @update-line-name="onUpdateLineName"
      @delete-line="onLinesModification"
      @delete-project="onDeleteProject"
      @edit-line="onEditLine"
      @validate-lines="onValidateLines"
    />
    <TrafficMap
      ref="trafficMap"
      :lines="lines"
      :current-project="currentProject"
      @new-lines-created="onLinesModification"
    />
  </section>
</template>
<style lang="scss" scoped>
.map-view {
  display: flex;
  height: calc(100% - 50px);
  width: 100%;
}
:deep(.mapboxgl-ctrl-bottom-right) {
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
}
:deep(.mapboxgl-ctrl-top-right) {
  visibility: v-bind(hiddenIfNoCurrentProject); /* stylelint-disable-line value-keyword-case */
}
:deep(.mapboxgl-ctrl.mapboxgl-ctrl-attrib) {
  display: none;
}
.mapview-disabled {
  position: absolute;
  width: 100%;
  height: calc(100% - 50px);
  background-color: rgb($grey-dark, 0.46);
  color: $white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
}
.mapview-disabled > div {
  font-size: 2.5em;
  margin-bottom: 20px;
}
</style>
